import { composeThemeFromProps, Compose } from '@css-modules-theme/react';
import clsx from 'clsx';
import React, { useRef, useState, useEffect } from 'react';
import { useSpring, animated } from 'react-spring';
import { Enter, Space, ArrowDown, ArrowUp } from '../../utils/keycode.js';
import { useAccordionContext } from './context/useAccordionContext.js';
import defaultStyles from './styles/Accordion.module.scss.js';
import { focusNextSiblingOf, focusPreviousSiblingOf } from './util/focus.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import { Typography } from '../typography/Typography.js';
import { omit } from '../../utils/omit.js';

const AccordionItem = /*#__PURE__*/React.forwardRef((props, ref) => {
  const {
    children,
    name,
    header,
    theme,
    extra,
    disabled,
    showExpandIcon = true,
    ...rest
  } = props;
  const styles = composeThemeFromProps(defaultStyles, {
    theme
  }, {
    compose: Compose.Merge
  });
  const contentInnerRef = useRef(null);
  const [contentMaxHeight, setContentMaxHeight] = useState(0);
  const {
    isItemActive,
    expandIcon,
    collapseIcon,
    variant,
    expandIconPosition,
    getRegionId,
    getControlId,
    onChange,
    transitionDuration,
    disableChevronRotation
  } = useAccordionContext();
  const isExpanded = isItemActive(name);
  const icon = !isExpanded ? expandIcon : collapseIcon;
  const mergedIcon = disableChevronRotation ? expandIcon : icon;
  const handleKeyDown = (event, name) => {
    const keyCode = event.key;
    if (keyCode === Enter.key || keyCode === Space.key) {
      event.preventDefault();
      onChange(name);
    }
    if (event.target instanceof HTMLElement) {
      switch (keyCode) {
        case ArrowUp.key:
          {
            event.preventDefault();
            focusPreviousSiblingOf(event.target);
            break;
          }
        case ArrowDown.key:
          {
            event.preventDefault();
            focusNextSiblingOf(event.target);
            break;
          }
      }
    }
  };
  useEffect(() => {
    const calcContentMaxHeight = () => {
      contentInnerRef && contentInnerRef.current && setContentMaxHeight(contentInnerRef.current.scrollHeight);
    };
    calcContentMaxHeight();
    window.addEventListener('resize', () => calcContentMaxHeight());
    return () => window.removeEventListener('resize', () => calcContentMaxHeight());
  }, [contentInnerRef, contentMaxHeight, children]);
  const maxHeight = contentMaxHeight ? `${contentMaxHeight}px` : undefined;
  const springArgs = {
    scY: isExpanded ? -1 : 1,
    config: {
      duration: transitionDuration
    },
    height: isExpanded ? maxHeight : '0px'
  };
  const {
    ...springProps
  } = useSpring(springArgs);
  return /*#__PURE__*/jsxs("div", {
    ref: ref,
    className: clsx(styles.item, {
      [styles.default]: variant === 'default',
      [styles.contained]: variant === 'contained',
      [styles.filled]: variant === 'filled',
      [styles.separated]: variant === 'separated',
      [styles.no_icon]: !icon,
      [styles.disabled]: disabled
    }),
    "data-active": isExpanded || undefined,
    ...omit(rest, ['classnames']),
    children: [/*#__PURE__*/jsxs("button", {
      "aria-expanded": isExpanded,
      "aria-controls": getRegionId(name),
      "data-accordion-control": true,
      type: "button",
      disabled: disabled,
      id: getControlId(name),
      className: clsx(styles.control, {
        [styles.control_left]: expandIconPosition === 'left',
        [styles.control_right]: expandIconPosition === 'right',
        [styles.control_disabled]: disabled
      }),
      onKeyDown: event => handleKeyDown(event, name),
      onClick: event => {
        rest?.onClick?.(event);
        onChange(name);
      },
      children: [/*#__PURE__*/jsx(Typography, {
        tag: "span",
        variant: "heading",
        theme: {
          root: styles.title
        },
        children: header
      }), showExpandIcon && /*#__PURE__*/jsx("span", {
        className: styles.icon,
        children: mergedIcon
      }), extra && /*#__PURE__*/jsx("div", {
        className: styles.extra,
        children: extra
      })]
    }), /*#__PURE__*/jsx(animated.div, {
      style: {
        overflow: 'hidden',
        ...springProps
      },
      "aria-labelledby": getControlId(name),
      role: "region",
      id: getRegionId(name),
      className: clsx(styles.content, {
        [styles.expanded]: isExpanded
      }),
      children: /*#__PURE__*/jsx("div", {
        ref: contentInnerRef,
        className: styles.content_inner,
        children: children
      })
    })]
  });
});
AccordionItem.displayName = 'AccordionItem';

export { AccordionItem };
